import * as Yup from "yup";
import {
  isIssueTypePermitted,
  useTextValidation,
  useUnlimitedLengthTitleValidation,
  useTypeCategoryTitleValidation,
  useRootCauseCategoryTitleValidation,
  useTypeTitleValidation,
  useRootCauseTitleValidation,
  useIssueTemplateTitleValidation,
  useNumericFieldValidation,
} from "./validationUtils";
import { useCallback, useMemo } from "react";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getCustomFieldEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueCustomFields";
import {
  IssueCustomAttributeDefinition,
  IssueCustomAttributeType,
  IssueCustomAttributeMapping,
  IssueStatus,
  IssueFieldId,
  IssueStandardFieldIds,
} from "@adsk/acc-issues-types";
import { useGetCustomFieldMappingsByIssueTypeId } from "../hooks/issueCustomFieldMappingHooks";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useGetIsFieldRequiredByIssueTypeId } from "../hooks/requiredFieldsByTypeHooks";
import { useWatcherPropName } from "../hooks/watchers/useWatcherPropName";
import {
  getCurrentUserAllTypesForNewIssue,
  getCurrentUserPermittedTypesForNewIssue,
} from "@adsk/acc-issues-core/state/redux/ducks/currentUserPermissions";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";

export const MAX_ISSUE_TITLE_LENGTH = 100;
export const MAX_ISSUE_DESCRIPTION_LENGTH = 1000;
export const MAX_LOCATION_DETAILS_LENGTH = 250;
export const MAX_TEXT_CUSTOM_FIELD_LENGTH = 250;
export const MAX_PARAGRAPH_CUSTOM_FIELD_LENGTH = 2500;

/**
 * This hook returns the custom field schema object without enforcing required fields
 */
const useGetCustomFieldSchemaObject = () => {
  const customFieldEntitiesMap = useSelector(getCustomFieldEntitiesMap);

  const getSingleCustomFieldSchema = useGetSingleCustomFieldSchema();
  return useCallback(
    (customFieldMappings: IssueCustomAttributeMapping[]) => {
      return Object.fromEntries(
        customFieldMappings
          .map(({ attributeDefinitionId }) => {
            const attribute = customFieldEntitiesMap[
              attributeDefinitionId
            ] as IssueCustomAttributeDefinition;
            return [
              attributeDefinitionId,
              attribute && getSingleCustomFieldSchema({ attribute }),
            ];
          })
          .filter(([, validation]) => validation)
      );
    },
    [customFieldEntitiesMap, getSingleCustomFieldSchema]
  );
};

/**
 * This hook is very similar to useGetCustomFieldSchemaObject, but it enforces required fields by issue type and draft status
 */
const useGetCustomFieldSchemaObjectWithRequirednessByType = () => {
  const customFieldEntitiesMap = useSelector(getCustomFieldEntitiesMap);
  const getIsFieldRequiredByIssueTypeId = useGetIsFieldRequiredByIssueTypeId();
  const getSingleCustomFieldSchema = useGetSingleCustomFieldSchema();

  return useCallback(
    ({
      customFieldMappings,
      issueTypeId,
      status,
    }: {
      customFieldMappings: IssueCustomAttributeMapping[];
      issueTypeId: string;
      status: string;
    }) => {
      return Object.fromEntries(
        customFieldMappings
          .map(({ attributeDefinitionId }) => {
            const attribute = customFieldEntitiesMap[
              attributeDefinitionId
            ] as IssueCustomAttributeDefinition;
            const isRequired =
              status !== IssueStatus.Draft &&
              getIsFieldRequiredByIssueTypeId({
                issueFieldId: attribute.id,
                issueTypeId,
              });
            return [
              attributeDefinitionId,
              attribute &&
                getSingleCustomFieldSchema({ attribute, isRequired }),
            ];
          })
          .filter(([, validation]) => validation)
      );
    },
    [
      customFieldEntitiesMap,
      getSingleCustomFieldSchema,
      getIsFieldRequiredByIssueTypeId,
    ]
  );
};

export const useGetSingleCustomFieldSchema = () => {
  const { T } = useTranslations();
  const maxLengthTextValidation = useTextValidation({
    maxLength: MAX_TEXT_CUSTOM_FIELD_LENGTH,
  });
  const maxLengthParagraphValidation = useTextValidation({
    maxLength: MAX_PARAGRAPH_CUSTOM_FIELD_LENGTH,
  });
  const numericValidation = useNumericFieldValidation();
  const CUSTOM_ATTRIBUTE_SCHEMA_BY_TYPE = useMemo<{
    [type: string]: Yup.MixedSchema;
  }>(
    () =>
      ({
        [IssueCustomAttributeType.List]: Yup.string().nullable(),
        [IssueCustomAttributeType.Text]: maxLengthTextValidation,
        [IssueCustomAttributeType.Paragraph]: maxLengthParagraphValidation,
        [IssueCustomAttributeType.Numeric]: numericValidation,
        [IssueCustomAttributeType.Date]: Yup.date(),
        [IssueCustomAttributeType.Checkbox]: Yup.boolean(),
      } as const),
    [maxLengthTextValidation, maxLengthParagraphValidation, numericValidation]
  );

  return ({
    attribute,
    isRequired = false,
  }: {
    attribute: IssueCustomAttributeDefinition;
    isRequired?: boolean;
  }) => {
    let schema = CUSTOM_ATTRIBUTE_SCHEMA_BY_TYPE[attribute.dataType];
    if (isRequired) {
      schema = schema.required(T.translate("validation.required") as string);
    }
    return schema;
  };
};

export const useIssueValidationSchema = ({
  requireTitleField = true,
  fixedTypeId,
}: { requireTitleField?: boolean; fixedTypeId?: string } = {}) => {
  const issueValidationSchemaObj = useIssueValidationSchemaObject({
    requireTitleField,
    fixedTypeId,
  });
  return Yup.object().shape(issueValidationSchemaObj);
};

export const useIssueValidationSchemaObject = ({
  requireTitleField = true,
  fixedTypeId,
}: { requireTitleField?: boolean; fixedTypeId?: string } = {}) => {
  const { T } = useTranslations();
  const watcherProp = useWatcherPropName();
  const getIsFieldRequiredByIssueTypeId = useGetIsFieldRequiredByIssueTypeId();
  const getCustomFieldMappingsByIssueTypeId =
    useGetCustomFieldMappingsByIssueTypeId();
  const getCustomFieldSchemaObjectWithRequirednessByType =
    useGetCustomFieldSchemaObjectWithRequirednessByType();
  const { value: isVisibilityByTypeOn } = useFeatureFlag(
    FeatureFlag.VisibilityByType
  );
  const allTypes = useSelector(getCurrentUserAllTypesForNewIssue);
  const permittedTypeIds = useSelector(getCurrentUserPermittedTypesForNewIssue);

  return {
    title: useTextValidation({
      maxLength: MAX_ISSUE_TITLE_LENGTH,
      isRequired: requireTitleField,
    }),
    issueTypeId: Yup.string()
      .required(T.translate("validation.required") as string)
      .test(
        "fixed-type-must-be-permitted",
        T.translate("issueCreation.validation.fixedTypeNotPermitted") as string,
        value =>
          isIssueTypePermitted({
            value,
            fixedTypeId,
            isVisibilityByTypeOn,
            allTypes,
            permittedTypeIds,
          })
      ),
    status: Yup.string().required(T.translate("validation.required") as string),
    description: useIssueFieldValidationSchema({
      issueFieldId: IssueStandardFieldIds.Description,
      maxLength: MAX_ISSUE_DESCRIPTION_LENGTH,
    }),
    assignedTo: useIssueFieldValidationSchema({
      issueFieldId: IssueStandardFieldIds.Assignee,
    }),
    [watcherProp]: Yup.array().when(
      ["issueTypeId", "status"],
      (issueTypeId: string, status: string) => {
        const isRequired =
          status !== IssueStatus.Draft &&
          getIsFieldRequiredByIssueTypeId({
            issueTypeId,
            issueFieldId: IssueStandardFieldIds.Watchers,
          });
        let schema = Yup.array();

        if (isRequired) {
          schema = schema
            .required(T.translate("validation.required") as string)
            .min(1);
        }
        return schema;
      }
    ),
    locationId: useIssueFieldValidationSchema({
      issueFieldId: IssueStandardFieldIds.Location,
    }),
    locationDetails: useIssueFieldValidationSchema({
      issueFieldId: IssueStandardFieldIds.LocationDetails,
      maxLength: MAX_LOCATION_DETAILS_LENGTH,
    }),
    gpsCoordinates: Yup.mixed().test({
      test: value => {
        if (!value) {
          return true;
        }

        const { latitude, longitude } = value;
        const isLatitudeValid = Yup.number()
          .min(-90)
          .max(90)
          .isValidSync(latitude);
        const isLongitudeValid = Yup.number()
          .min(-180)
          .max(180)
          .isValidSync(longitude);

        if (!isLatitudeValid || !isLongitudeValid) {
          throw new Yup.ValidationError(
            T.translate("validation.gpsCoordinates") as string,
            value,
            "gpsCoordinates"
          );
        }

        return true;
      },
    }),
    dueDate: useIssueFieldValidationSchema({
      issueFieldId: IssueStandardFieldIds.DueDate,
    }),
    startDate: useIssueFieldValidationSchema({
      issueFieldId: IssueStandardFieldIds.StartDate,
    }),
    rootCauseId: useIssueFieldValidationSchema({
      issueFieldId: IssueStandardFieldIds.RootCauseWithCategory,
    }),
    // custom field validation is conditional to the current issue type.
    customAttributes: Yup.object().when(
      ["issueTypeId", "status"],
      (issueTypeId: string, status: string) => {
        const customFieldMappings =
          getCustomFieldMappingsByIssueTypeId(issueTypeId);
        const customFieldSchemaObject =
          getCustomFieldSchemaObjectWithRequirednessByType({
            issueTypeId,
            status,
            customFieldMappings,
          });
        return Yup.object().shape(customFieldSchemaObject);
      }
    ),
  };
};

export const useIssueBulkEditValidationSchema = () => {
  const getCustomFieldMappingsByIssueTypeId =
    useGetCustomFieldMappingsByIssueTypeId();
  const getCustomFieldSchemaObject = useGetCustomFieldSchemaObject();
  return Yup.object().shape({
    title: useTextValidation({
      maxLength: MAX_ISSUE_TITLE_LENGTH,
    }),
    status: Yup.string(),
    locationDetails: useTextValidation({
      maxLength: MAX_LOCATION_DETAILS_LENGTH,
    }),
    description: useTextValidation({ maxLength: MAX_ISSUE_DESCRIPTION_LENGTH }),
    // custom field validation is conditional to the current issue type.
    customAttributes: Yup.object().when(
      "issueTypeId",
      (issueTypeId: string) => {
        const customFieldMappings =
          getCustomFieldMappingsByIssueTypeId(issueTypeId);
        const customFieldSchemaObject =
          getCustomFieldSchemaObject(customFieldMappings);
        return Yup.object().shape(customFieldSchemaObject);
      }
    ),
  });
};

export const useIssueTypeValidationSchema = ({
  categoryId,
  titlesExcludedFromValidation,
}: {
  categoryId?: string;
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const { T } = useTranslations();
  return Yup.object().shape({
    title: useTypeTitleValidation({
      categoryId,
      titlesExcludedFromValidation,
    }),
    code: Yup.string().max(
      3,
      T.translate("validation.maxLength", { maxLength: 3 }) as string
    ),
    isActive: Yup.boolean().required(
      T.translate("validation.required") as string
    ),
    issueTypeCategoryId: Yup.string().required(
      T.translate("validation.required") as string
    ),
  });
};

// This is a hook because it depends on a hook
export const useTypeCategoryValidationSchema = ({
  titlesExcludedFromValidation,
}: {
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const { T } = useTranslations();
  return Yup.object().shape({
    title: useTypeCategoryTitleValidation({
      titlesExcludedFromValidation,
    }),
    isActive: Yup.boolean().required(
      T.translate("validation.required") as string
    ),
    issueTypeTitles: Yup.array().of(useTypeTitleValidation()),
  });
};

export const useRootCauseCategoryValidationSchema = ({
  titlesExcludedFromValidation,
}: {
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const { T } = useTranslations();
  return Yup.object().shape({
    title: useRootCauseCategoryTitleValidation({
      titlesExcludedFromValidation,
    }),
    isActive: Yup.boolean().required(
      T.translate("validation.required") as string
    ),
    rootCauseTitles: Yup.array().of(useRootCauseTitleValidation()),
  });
};

export const useRootCauseValidationSchema = ({
  categoryId,
  titlesExcludedFromValidation,
}: {
  categoryId?: string;
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const { T } = useTranslations();
  return Yup.object().shape({
    title: useRootCauseTitleValidation({
      categoryId,
      titlesExcludedFromValidation,
    }),
    isActive: Yup.boolean().required(
      T.translate("validation.required") as string
    ),
    rootCauseCategoryId: Yup.string().required(
      T.translate("validation.required") as string
    ),
  });
};

export const useCustomFieldValidationSchema = () => {
  const { T } = useTranslations();
  return Yup.object({
    title: useTextValidation({ maxLength: 100, isRequired: true }),
    dataType: Yup.string().required(
      T.translate("validation.required") as string
    ),
    description: useTextValidation({ maxLength: 500 }),
    options: Yup.array()
      .of(useUnlimitedLengthTitleValidation())
      .when("dataType", {
        is: IssueCustomAttributeType.List,
        then: Yup.array().required(
          T.translate("validation.required") as string
        ),
        otherwise: Yup.array().min(0),
      }),
  });
};

export const useIssueTemplateValidationSchema = ({
  titlesExcludedFromValidation,
}: {
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const numericValidation = useNumericFieldValidation({
    requirePositive: true,
  });
  return Yup.object().shape({
    title: useIssueTemplateTitleValidation({
      titlesExcludedFromValidation,
    }),
    description: useTextValidation({ maxLength: MAX_ISSUE_DESCRIPTION_LENGTH }),
    locationDetails: useTextValidation({
      maxLength: MAX_LOCATION_DETAILS_LENGTH,
    }),
    daysTilDueDate: numericValidation,
  });
};

const useIssueFieldValidationSchema = ({
  issueFieldId,
  maxLength,
}: {
  issueFieldId: IssueFieldId;
  maxLength?: number;
}) => {
  const { T } = useTranslations();
  const getIsFieldRequiredByIssueTypeId = useGetIsFieldRequiredByIssueTypeId();
  return Yup.string().when(
    ["issueTypeId", "status"],
    (issueTypeId: string, status: string) => {
      const isRequired =
        status !== IssueStatus.Draft &&
        getIsFieldRequiredByIssueTypeId({
          issueTypeId,
          issueFieldId,
        });
      let schema = Yup.string().trim().nullable(true);

      if (maxLength !== undefined) {
        schema = schema.max(
          maxLength,
          T.translate("validation.maxLength", { maxLength }) as string
        );
      }

      if (isRequired) {
        schema = schema.required(T.translate("validation.required") as string);
      }
      return schema;
    }
  );
};
